/**
 * Group Ledger Screen — M_GroupLedger CRUD
 * Left: Form | Right: Chart of Accounts Tree
 * Fields: Group Ledger Code, Group Ledger Name*, Primary Group Name* (search), Parent Group Name (search), Place of Head
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Save, Grid3X3, XCircle, Plus, RefreshCw, Search, ArrowLeft } from 'lucide-react';
import ERPAdvancedGrid from '../../components/erp/ERPAdvancedGrid';
import ChartOfAccountsTree from '../../components/erp/ChartOfAccountsTree';
import ERPLookupModal from '../../components/erp/ERPLookupModal';
import DynamicFieldRenderer from '../../components/erp/DynamicFieldRenderer';
import { erpToast } from '../../components/erp/ERPToast';
import { FormHelp } from '../../components/help-engine/FormHelp';
import { HelpInfo } from '../../components/help-engine/HelpInfo';
import '../../styles/erp-theme.css';
import { API_BASE as API_URL, getToken} from '../../lib/api';

const token = () => getToken() || '';
const hdrs = () => ({ Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' });

const EMPTY = { GroupCode: '', GroupName: '', PrimaryGroupName: '', PrimaryGroupId: '', ParentGroupName: '', ParentGroupId: '', PlaceOfHead: '0' };

const GroupLedgerScreen = ({ theme = 'light', navigateTo = () => {} }) => {
  const [view, setView] = useState('grid');
  const [allItems, setAllItems] = useState([]);
  const [allData, setAllData] = useState({ primaryGroups: [], groupLedgers: [] });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [mode, setMode] = useState('add');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [treeKey, setTreeKey] = useState(0);
  const [lookupOpen, setLookupOpen] = useState(null);
  const token = getToken() || '';
  const userRole = (() => { try { return JSON.parse(localStorage.getItem('retailcliq_user') || '{}').role || ''; } catch { return ''; } })();
  const [dynValues, setDynValues] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/finance/chart-of-accounts`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) {
        const items = j.data?.groupLedgers || [];
        setAllItems(items);
        setAllData({ primaryGroups: j.data?.primaryGroups || [], groupLedgers: items });
      }
    } catch { erpToast.error('Failed to load'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const COLUMNS = useMemo(() => [
    { field: 'GroupId', header: 'Code' },
    { field: 'GroupName', header: 'Group Ledger Name' },
    { field: 'PrimaryGroup', header: 'Primary Group' },
    { field: 'ParentGroup', header: 'Parent Group' },
    { field: 'PlaceOfHead', header: 'Place of Head' },
    { field: 'Status', header: 'Status' },
  ], []);

  const { data, filteredTotal } = useMemo(() => {
    let items = [...allItems];
    Object.entries(filters).forEach(([key, val]) => {
      if (val) { const s = val.toLowerCase(); items = items.filter(i => String(i[key] || '').toLowerCase().includes(s)); }
    });
    const filteredTotal = items.length;
    if (sortField) {
      items.sort((a, b) => {
        const av = String(a[sortField] || ''), bv = String(b[sortField] || '');
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      });
    }
    return { data: items.slice((page - 1) * pageSize, page * pageSize), filteredTotal };
  }, [allItems, filters, sortField, sortDir, page, pageSize]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const isView = mode === 'view';

  const openForm = (item, m) => {
    if (item) {
      const pg = allData.primaryGroups.find(p => p.PGroupId === item.PrimaryGroup);
      setForm({ GroupCode: item.GroupId, GroupName: item.GroupName, PrimaryGroupName: pg?.PGroupName || item.PrimaryGroup, PrimaryGroupId: item.PrimaryGroup, ParentGroupName: item.ParentGroup || '', ParentGroupId: item.ParentGroupId || '', PlaceOfHead: item.PlaceOfHead || '0' });
    } else { setForm({ ...EMPTY }); }
    setMode(m || 'add'); setView('form'); setError('');
  };

  const handleTreeSelect = (node) => {
    if (node.type === 'group') {
      const item = allData.groupLedgers.find(d => d.GroupId === node.code);
      if (item) openForm(item, 'edit');
    }
  };

  const handleSave = async () => {
    if (!form.GroupName?.trim()) { setError('Group Ledger Name is required'); return; }
    if (!form.PrimaryGroupId) { setError('Primary Group Name is required'); return; }
    setSaving(true); setError('');
    try {
      const isEdit = mode === 'edit' && form.GroupCode;
      const url = isEdit ? `${API_URL}/api/finance/group-ledgers/${form.GroupCode}` : `${API_URL}/api/finance/group-ledgers`;
      const method = isEdit ? 'PUT' : 'POST';
      const payload = { GroupName: form.GroupName, PrimaryGroup: form.PrimaryGroupId, GLevel: '2', ParentGroup: form.ParentGroupId || 'Null', Slno: form.PlaceOfHead || 0 };
      const r = await fetch(url, { method, headers: hdrs(), body: JSON.stringify(payload) });
      const j = await r.json();
      if (j.success) {
        erpToast.success(j.message || (mode === 'edit' ? 'Group Ledger updated' : 'Group Ledger saved'));
        if (j.GroupId) setForm(p => ({ ...p, GroupCode: j.GroupId }));
        if (mode === 'edit') setMode('add');
        setTreeKey(k => k + 1);
        loadData();
      } else { setError(j.error || 'Save failed'); }
    } catch (e) { setError('Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Void this group ledger?')) return;
    try {
      const r = await fetch(`${API_URL}/api/finance/group-ledgers/${id}`, { method: 'DELETE', headers: hdrs() });
      const j = await r.json();
      if (j.success) { erpToast.success('Group Ledger voided'); loadData(); setTreeKey(k => k + 1); }
      else erpToast.error(j.error || 'Void failed');
    } catch { erpToast.error('Failed to void'); }
  };

  if (view === 'grid') {
    return (
      <div className="erp-module" style={{ flexDirection: 'row' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <ERPAdvancedGrid
            title="Group Ledger"
            columns={COLUMNS}
            data={data}
            total={filteredTotal}
            idField="GroupId"
            nameField="GroupName"
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
            filters={filters}
            onFilterChange={(f) => { setFilters(f); setPage(1); }}
            loading={loading}
            onAddNew={() => openForm(null, 'add')}
            onEdit={(row, m) => openForm(row, m)}
            onVoid={(row) => handleDelete(row.GroupId)}
            onBack={() => navigateTo('HUB')}
            testId="group-ledger"
            exportFileName="GroupLedger"
            cellRenderer={(field, value) => {
              if (field === 'ParentGroup') return value || '\u2014';
              if (field === 'Status') return <span className={`erp-status-badge ${value === 'S' ? 'active' : 'inactive'}`}>{value === 'S' ? 'Active' : 'Inactive'}</span>;
              return null;
            }}
          />
        </div>
        <div style={{ width: 280, flexShrink: 0 }}><ChartOfAccountsTree onSelect={handleTreeSelect} refreshKey={treeKey} /></div>
      </div>
    );
  }

  // ─── FORM ───
  return (
    <FormHelp formName="GroupLedger" userRole={userRole}>
    <div className="erp-module" style={{ flexDirection: 'row' }} data-dev-component="GroupLedger" data-dev-file="screens/finance/GroupLedgerScreen.js">
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div className="erp-screen-header">
          <button onClick={() => setView('grid')} data-testid="screen-back-btn" style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",padding:"2px 0"}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 5-7 7 7 7"/></svg><span className="erp-screen-title" style={{color:"var(--erp-accent,#1856a7)"}}>Group Ledger</span></button>
          <div style={{ fontSize: 12, fontWeight: 700, fontFamily: 'var(--erp-font-mono)', color: 'var(--erp-primary)' }}>{form.GroupCode || 'New'}</div>
          <div className="erp-screen-actions">
            {!isView && <button data-testid="gl-save-btn" className="erp-btn erp-btn-ghost" onClick={handleSave} disabled={saving}>{mode === 'edit' ? <RefreshCw size={13} /> : <Save size={13} />} {saving ? 'Saving...' : mode === 'edit' ? 'Update' : 'Save'}</button>}
            <button className="erp-btn erp-btn-ghost" onClick={() => { setView('grid'); loadData(); }}><Grid3X3 size={13} /> Gridview</button>
            {!isView && <button className="erp-btn erp-btn-ghost" onClick={() => openForm(null, 'add')}><XCircle size={13} /> Clear</button>}
          </div>
        </div>
        {error && <div className="erp-alert-error">{error}</div>}
        <form style={{ padding: 'var(--erp-sp-4)', flex: 1, overflow: 'auto' }} onSubmit={e => e.preventDefault()}>
          <div className="erp-form-card"><div className="erp-form-section">
            <div style={{ maxWidth: 420 }}>
              <div className="erp-form-row"><label className="erp-form-label">Group Ledger Code<HelpInfo controlId="GroupCode" controlName="Group Ledger Code" /></label><div className="erp-form-control" style={{ display: 'flex', gap: 4 }}>
                <input data-testid="grpl-code" data-help-control="GroupCode" value={form.GroupCode} disabled className="erp-input" style={{ fontFamily: 'var(--erp-font-mono)', flex: 1 }} />
                <button className="erp-btn-icon" style={{ border: '1px solid var(--erp-border)' }}><Search size={12} /></button>
              </div></div>
              <div className="erp-form-row"><label className="erp-form-label required">Group Ledger Name<HelpInfo controlId="GroupName" controlName="Group Ledger Name" /></label><div className="erp-form-control"><input data-testid="grpl-name" data-help-control="GroupName" value={form.GroupName} onChange={e => set('GroupName', e.target.value)} disabled={isView} className="erp-input" /></div></div>
              <div className="erp-form-row"><label className="erp-form-label required">Primary Group Name<HelpInfo controlId="PrimaryGroupName" controlName="Primary Group Name" /></label><div className="erp-form-control" style={{ display: 'flex', gap: 4 }}>
                <input data-testid="grpl-pgname" data-help-control="PrimaryGroupName" value={form.PrimaryGroupName} readOnly className="erp-input" style={{ flex: 1 }} />
                <button className="erp-btn-icon" style={{ border: '1px solid var(--erp-border)' }} onClick={() => setLookupOpen('primary')} disabled={isView}><Search size={12} /></button>
              </div></div>
              <div className="erp-form-row"><label className="erp-form-label">Parent Group Name<HelpInfo controlId="ParentGroupName" controlName="Parent Group Name" /></label><div className="erp-form-control" style={{ display: 'flex', gap: 4 }}>
                <input data-testid="grpl-parent" data-help-control="ParentGroupName" value={form.ParentGroupName} readOnly className="erp-input" style={{ flex: 1 }} />
                <button className="erp-btn-icon" style={{ border: '1px solid var(--erp-border)' }} onClick={() => setLookupOpen('parent')} disabled={isView}><Search size={12} /></button>
              </div></div>
              <div className="erp-form-row"><label className="erp-form-label">Place of Head<HelpInfo controlId="PlaceOfHead" controlName="Place of Head" /></label><div className="erp-form-control"><input data-testid="grpl-poh" data-help-control="PlaceOfHead" value={form.PlaceOfHead} onChange={e => set('PlaceOfHead', e.target.value)} disabled={isView} className="erp-input" type="number" /></div></div>
            </div>
            <DynamicFieldRenderer screenName="GroupLedger" recordId={form.GroupCode} token={token} values={dynValues} onChange={(k, v) => setDynValues(p => ({ ...p, [k]: v }))} readOnly={isView} userRole={userRole} />
          </div></div>
        </form>
      </div>
      <div style={{ width: 280, flexShrink: 0 }}><ChartOfAccountsTree onSelect={handleTreeSelect} selectedId={form.GroupCode} refreshKey={treeKey} /></div>
      {lookupOpen === 'primary' && (
        <ERPLookupModal
          title="Select Primary Group"
          data={allData.primaryGroups.map(p => ({ id: p.PGroupId, name: p.PGroupName, extra: p.Category === 'BS' ? 'Balance Sheet' : 'P&L' }))}
          columns={[{ field: 'id', label: 'Code' }, { field: 'name', label: 'Name' }, { field: 'extra', label: 'Category' }]}
          onSelect={(item) => { set('PrimaryGroupId', item.id); set('PrimaryGroupName', item.name); setLookupOpen(null); }}
          onClose={() => setLookupOpen(null)}
        />
      )}
      {lookupOpen === 'parent' && (
        <ERPLookupModal
          title="Select Parent Group"
          data={allData.groupLedgers.map(g => ({ id: g.GroupId, name: g.GroupName, extra: g.PrimaryGroup }))}
          columns={[{ field: 'id', label: 'Code' }, { field: 'name', label: 'Name' }, { field: 'extra', label: 'Primary Group' }]}
          onSelect={(item) => { set('ParentGroupId', item.id); set('ParentGroupName', item.name); setLookupOpen(null); }}
          onClose={() => setLookupOpen(null)}
        />
      )}
    </div>
    </FormHelp>
  );
};

export default GroupLedgerScreen;
